// src/pages/ProfilePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaArrowLeft,
  FaPencilAlt,
  FaSignOutAlt,
  FaCalendarAlt,
  FaTag,
  FaVolumeUp,
  FaVolumeMute,
  FaMobileAlt,
  FaMicrophone,
  FaMicrophoneSlash,
  FaTrashAlt,
  FaTimes,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaQrcode,
  FaLink,
} from "react-icons/fa";
import { writeStoredSession } from "../utils/accountLinks";
import { resetLogRocketSession } from "../utils/logRocket";
import { APP_ESCAPE_EVENT, APP_SHORTCUT_EVENT, focusAndSelect } from "../utils/shortcuts";

const color = {
  brand: "#015b9a",
  brandSoft: "#e7f2fb",
  text: "#111827",
  muted: "#6B7280",
  line: "#E5E7EB",
  bg: "#F2F5F9",
  danger: "#D32F2F",
  dangerSoft: "#FFF5F5",
  white: "#fff",
};

const VOICE_KEYS = {
  languageKey: "bill_voice_language", // "hi" | "en"
  enabledKey: "bill_voice_enabled", // "true" | "false"
};

const parseVersionValue = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const match = raw.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const getServerVersionCode = (userData = {}) => {
  const candidates = [
    "versioncode",
    "versionCode",
    "version_code",
    "appversioncode",
    "appVersionCode",
    "app_version_code",
    "required_version",
    "requiredVersionCode",
    "latestVersionCode",
    "requiredversioncode",
    "version",
  ];
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(userData, key)) {
      const parsed = parseVersionValue(userData[key]);
      if (parsed !== null) return parsed;
    }
  }
  return null;
};

const fetchRequiredVersionFromCheckValid = async (restaurantId) => {
  if (!restaurantId) return null;
  try {
    const response = await fetch(
      "https://qr.billsarthi.in/public/api/checkvalid",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: restaurantId }),
      }
    );
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return null;
    return parseVersionValue(result?.version);
  } catch (error) {
    return null;
  }
};

const ProfilePage = () => {
  const navigate = useNavigate();

  // ----- view state -----
  const [user, setUser] = useState({});
  const [billVoiceEnabled, setBillVoiceEnabled] = useState(false);
  const [billVoiceLang, setBillVoiceLang] = useState("en");
  const [updateRequired, setUpdateRequired] = useState(false);
  const [serverVersionCode, setServerVersionCode] = useState(null);

  // delete modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // ----- edit form state (mirrors mobile EditProfile) -----
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [shopName, setShopName] = useState("");
  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [addr3, setAddr3] = useState("");
  const [cf1, setCf1] = useState("");
  const [cf2, setCf2] = useState("");
  const [cf3, setCf3] = useState("");
  const [gst, setGst] = useState("");
  const [upi, setUpi] = useState("");
  const [sound, setSound] = useState(1);
  const [vibration, setVibration] = useState(1);
  const [showLogo, setShowLogo] = useState(1);
  const [showQr, setShowQr] = useState(1);
  const [showPhone, setShowPhone] = useState(1);

  // avatar
  const [photoPreview, setPhotoPreview] = useState(null); // URL
  const [photoFile, setPhotoFile] = useState(null); // File
  const [photoChanged, setPhotoChanged] = useState(false);
  const editNameInputRef = useRef(null);
  const saveProfileRef = useRef(null);

  const localVersionCode = useMemo(() => {
    const fromEnv =
      process.env.REACT_APP_BUILD_VERSION ||
      process.env.REACT_APP_VERSION ||
      process.env.npm_package_version ||
      "0";
    return parseVersionValue(fromEnv) || 0;
  }, []);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!saved) {
      toast.error("No user data found");
      navigate("/login");
      return;
    }
    setUser(saved);

    // update check (mirror mobile)
    const primeVersion = async () => {
      const serverFromProfile = getServerVersionCode(saved);
      const required = await fetchRequiredVersionFromCheckValid(saved?.id);
      const chosen = required ?? serverFromProfile;
      setServerVersionCode(chosen);
      setUpdateRequired(
        chosen !== null && Number(chosen) > Number(localVersionCode || 0)
      );
    };
    primeVersion();

    // voice prefs mirror
    const lang = localStorage.getItem(VOICE_KEYS.languageKey);
    const enabledRaw = localStorage.getItem(VOICE_KEYS.enabledKey);
    const enabledParsed =
      enabledRaw === null || enabledRaw === undefined
        ? true
        : !["false", "0"].includes(String(enabledRaw).toLowerCase());
    setBillVoiceLang(lang === "hi" ? "hi" : "en");
    setBillVoiceEnabled(enabledParsed);

    // prime edit form with current user values
    primeEditForm(saved);
  }, [navigate, localVersionCode]);

  const primeEditForm = (u) => {
    setName(u?.name || "");
    setEmail(u?.email || "");
    setShopName(u?.restaurant_name || "");
    setAddr1(u?.addressline1 || "");
    setAddr2(u?.addressline2 || "");
    setAddr3(u?.addressline3 || "");
    setCf1(u?.customfield1 || "");
    setCf2(u?.customfield2 || "");
    setCf3(u?.customfield3 || "");
    setGst(u?.gstno || "");
    setUpi(u?.upiaddress || "");
    setSound(u?.issoundenable ?? 1);
    setVibration(u?.isvibrationenable ?? 1);
    setShowLogo(
      typeof u?.showlogoonprinter === "number" ? u.showlogoonprinter : 1
    );
    setShowQr(
      typeof u?.showqrcodeonprinter === "number" ? u.showqrcodeonprinter : 1
    );
    setShowPhone(
      typeof u?.showphonenumberonprinter === "number"
        ? u.showphonenumberonprinter
        : 1
    );

    // avatar preview (remote ok)
    let url = u?.avatar_url || u?.photo;
    if (url && !String(url).startsWith("http"))
      url = `https://qr.billsarthi.com/public/${url}`;
    setPhotoPreview(url || "/logo192.png");
    setPhotoFile(null);
    setPhotoChanged(false);
  };

  const yesNo = (v) => (String(v) === "1" || v === 1 ? "Yes" : "No");

  const avatarUrl = useMemo(() => {
    if (photoPreview) return photoPreview;
    let url = user?.avatar_url || user?.photo;
    if (url && !String(url).startsWith("http"))
      url = `https://qr.billsarthi.com/public/${url}`;
    return url || "/logo192.png";
  }, [user, photoPreview]);

  const planName = user?.package_type
    ? user.package_type.charAt(0).toUpperCase() + user.package_type.slice(1)
    : "No Plan";

  const validity = user?.validupto
    ? new Date(user.validupto).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "No Validity";

  const voiceLabel = billVoiceEnabled
    ? billVoiceLang === "hi"
      ? "Hindi"
      : "English"
    : "Off";
  const versionSubtitle = updateRequired
    ? serverVersionCode
      ? `Version ${serverVersionCode} is required`
      : "New build available"
    : "You are on the latest web build.";

  const openStoreLink = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.billsarthi",
      "_blank"
    );
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      resetLogRocketSession();
      localStorage.removeItem("userData");
      toast.info("Logged out");
      navigate("/login");
    }
  };

  const openDelete = () => {
    setConfirmText("");
    setConfirmOpen(true);
  };
  const confirmMatches = confirmText.trim().toUpperCase() === "DELETE";

  const actuallyDeleteAccount = async () => {
    try {
      setDeleting(true);
      const id = user?.id;
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/account/delete",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: id }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      resetLogRocketSession();
      localStorage.removeItem("userData");
      toast.success("Account deleted");
      navigate("/login");
    } catch (e) {
      toast.error("Unable to delete account. Please try again.");
      console.error(e);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  // ---------- edit modal helpers ----------
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setPhotoChanged(true);
  };
  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoChanged(true); // treat as removal if backend supports flag
  };

  const saveProfile = async () => {
    const saved = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = saved?.data?.id;
    if (!uid) {
      toast.error("User not found in storage");
      return;
    }

    setSaving(true);
    try {
      const form = new FormData();
      form.append("restaurantid", String(uid));
      form.append("restaurant_name", shopName ?? "");
      form.append("name", name ?? "");
      form.append("email", email ?? "");
      form.append("addressline1", addr1 ?? "");
      form.append("addressline2", addr2 ?? "");
      form.append("addressline3", addr3 ?? "");
      form.append("issoundenable", String(sound ?? 0));
      form.append("isvibrationenable", String(vibration ?? 0));
      form.append("customfield1", cf1 ?? "");
      form.append("customfield2", cf2 ?? "");
      form.append("customfield3", cf3 ?? "");
      form.append("gstno", gst ?? "");
      form.append("upiaddress", upi ?? "");
      form.append("showlogoonprinter", String(showLogo ?? 0));
      form.append("showqrcodeonprinter", String(showQr ?? 0));
      form.append("showphonenumberonprinter", String(showPhone ?? 0));

      // Only attach if changed & we have a new File
      if (photoChanged && photoFile instanceof File) {
        form.append("photo", photoFile, photoFile.name || "avatar.jpg");
      }
      // If you want to signal removal explicitly (only if your backend supports it):
      // if (photoChanged && !photoFile) form.append("removephoto", "1");

      const resp = await fetch(
        "https://qr.billsarthi.in/public/api/edit/profile",
        {
          method: "POST",
          body: form, // don't set Content-Type when using FormData
        }
      );

      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result?.success === false) {
        throw new Error(result?.message || `HTTP ${resp.status}`);
      }

      // Merge returned data (if any), else our local fields
      const prev = JSON.parse(localStorage.getItem("userData") || "{}");
      const prevData = prev?.data || {};
      const updatedUser = result?.data
        ? { ...prevData, ...(result.data || {}) }
        : {
            ...prevData,
            restaurant_name: shopName,
            name,
            email,
            addressline1: addr1,
            addressline2: addr2,
            addressline3: addr3,
            issoundenable: sound,
            isvibrationenable: vibration,
            customfield1: cf1,
            customfield2: cf2,
            customfield3: cf3,
            gstno: gst,
            upiaddress: upi,
            showlogoonprinter: showLogo,
            showqrcodeonprinter: showQr,
            showphonenumberonprinter: showPhone,
          };

      writeStoredSession(
        { ...(prev || {}), success: true, data: updatedUser },
        { preserveAccountLinkContext: true }
      );

      // persist voice prefs on web
      localStorage.setItem(VOICE_KEYS.languageKey, billVoiceLang);
      localStorage.setItem(
        VOICE_KEYS.enabledKey,
        billVoiceEnabled ? "true" : "false"
      );

      // reflect on screen
      setUser(updatedUser);
      toast.success("Profile updated");
      setEditOpen(false);
    } catch (err) {
      console.error("Edit profile error:", err);
      toast.error(err.message || "Error updating profile");
    } finally {
      setSaving(false);
    }
  };
  saveProfileRef.current = saveProfile;

  useEffect(() => {
    if (!editOpen) return undefined;
    const timeoutId = setTimeout(() => {
      focusAndSelect(editNameInputRef.current);
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [editOpen]);

  useEffect(() => {
    const onShortcut = (event) => {
      switch (event?.detail?.id) {
        case "profile.edit":
          if (confirmOpen) return;
          event.preventDefault();
          setEditOpen(true);
          return;
        case "profile.save":
          if (!editOpen || saving || confirmOpen) return;
          event.preventDefault();
          saveProfileRef.current?.();
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [confirmOpen, editOpen, saving]);

  useEffect(() => {
    const onEscape = (event) => {
      if (confirmOpen) {
        setConfirmOpen(false);
        event.preventDefault();
        return;
      }
      if (editOpen) {
        setEditOpen(false);
        event.preventDefault();
      }
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [confirmOpen, editOpen]);

  return (
    <Wrap>
      <ToastContainer position="top-right" autoClose={2000} />
     
      <Main>
        
        <HeroCard>
          <Avatar>
            <img src={avatarUrl} alt="avatar" />
          </Avatar>
          <Name>{user?.name || "—"}</Name>
          <Shop>{user?.restaurant_name || "—"}</Shop>

          <Badges>
            <Badge>
              <FaTag color={color.brand} />
              <span>
                Plan: <b>{planName}</b>
              </span>
            </Badge>
            <Badge>
              <FaCalendarAlt color={color.brand} />
              <span>
                Valid upto: <b>{validity}</b>
              </span>
            </Badge>
          </Badges>

        
        </HeroCard>

        <Section>
          <SectionTitle>Account Details</SectionTitle>

          <Grid>
            <DataCard label="Email" value={user?.email} />
            <DataCard label="Phone" value={user?.mobile_number} />
            <DataCard label="Address Line 1" value={user?.addressline1} />
            <DataCard label="Address Line 2" value={user?.addressline2} />
            <DataCard label="Address Line 3" value={user?.addressline3} />
            <DataCard label="Custom Field 1" value={user?.customfield1} />
            <DataCard label="Custom Field 2" value={user?.customfield2} />
            <DataCard label="Custom Field 3" value={user?.customfield3} />
            <DataCard label="GST No." value={user?.gstno} />
            <DataCard label="UPI Address" value={user?.upiaddress} />
            <DataCard
              label="Logo on Bill"
              value={yesNo(user?.showlogoonprinter ?? 0)}
            />
            <DataCard
              label="Phone on Bill"
              value={yesNo(user?.showphonenumberonprinter ?? 1)}
            />
            <DataCard
              label="QRCode on Bill"
              value={yesNo(user?.showqrcodeonprinter ?? 0)}
            />
          </Grid>

          <ScanBtn onClick={() => navigate("/menu-scanner")}>
            <FaQrcode />
            <span>Scan Menu with AI</span>
          </ScanBtn>

          <ScanBtn onClick={() => navigate("/linked-restaurants")}>
            <FaLink />
            <span>Linked Restaurants</span>
          </ScanBtn>

          <CtaRow>
            <OutlineBtn onClick={() => setEditOpen(true)}>
              <FaPencilAlt color={color.brand} />
              <span>Edit Profile</span>
            </OutlineBtn>

            <DangerOutlineBtn onClick={openDelete}>
              <FaTrashAlt color={color.danger} />
              <span>Delete Account</span>
            </DangerOutlineBtn>
          </CtaRow>
        </Section>
      </Main>

      {/* --- Edit Modal (in-page) --- */}
      {editOpen && (
        <ModalOverlay
          onMouseDown={(e) =>
            e.target === e.currentTarget && setEditOpen(false)
          }
        >
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Profile</ModalTitle>
              <CloseBtn onClick={() => setEditOpen(false)} aria-label="Close">
                <FaTimes />
              </CloseBtn>
            </ModalHeader>

            <ModalBody>
              {/* Avatar */}
              <AvatarRow>
                <AvatarBig>
                  {photoPreview ? (
                    <img src={photoPreview} alt="avatar" />
                  ) : (
                    <div className="ph" />
                  )}
                </AvatarBig>
                <div>
                  <FileInputLabel htmlFor="photoPick">
                    <FaUpload />
                    <span>Choose Photo</span>
                  </FileInputLabel>
                  <HiddenFile
                    id="photoPick"
                    accept="image/*"
                    type="file"
                    onChange={onPickFile}
                  />
                  {photoPreview && (
                    <RemoveBtn type="button" onClick={removePhoto}>
                      Remove
                    </RemoveBtn>
                  )}
                  <Hint>JPG/PNG, ideally square ~250×250.</Hint>
                </div>
              </AvatarRow>

              <FormGrid>
                <Field>
                  <Label>Name</Label>
                  <Input
                    ref={editNameInputRef}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </Field>
                <Field>
                  <Label>Email</Label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    autoComplete="email"
                  />
                </Field>
                <Field>
                  <Label>Shop Name</Label>
                  <Input
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    placeholder="Restaurant / Shop"
                  />
                </Field>

                <Field col="2">
                  <Label>Address Line 1</Label>
                  <Input
                    value={addr1}
                    onChange={(e) => setAddr1(e.target.value)}
                    placeholder="Address line 1"
                  />
                </Field>
                <Field>
                  <Label>Address Line 2</Label>
                  <Input
                    value={addr2}
                    onChange={(e) => setAddr2(e.target.value)}
                    placeholder="Address line 2"
                  />
                </Field>
                <Field>
                  <Label>Address Line 3</Label>
                  <Input
                    value={addr3}
                    onChange={(e) => setAddr3(e.target.value)}
                    placeholder="Address line 3"
                  />
                </Field>

                <Field>
                  <Label>Custom Field 1</Label>
                  <Input
                    value={cf1}
                    onChange={(e) => setCf1(e.target.value)}
                    placeholder="e.g. FSSAI no."
                  />
                </Field>
                <Field>
                  <Label>Custom Field 2</Label>
                  <Input
                    value={cf2}
                    onChange={(e) => setCf2(e.target.value)}
                    placeholder="Custom field 2"
                  />
                </Field>
                <Field>
                  <Label>Custom Field 3</Label>
                  <Input
                    value={cf3}
                    onChange={(e) => setCf3(e.target.value)}
                    placeholder="Custom field 3"
                  />
                </Field>

                <Field>
                  <Label>GST No.</Label>
                  <Input
                    value={gst}
                    onChange={(e) => setGst(e.target.value)}
                    placeholder="GSTIN"
                  />
                </Field>
                <Field>
                  <Label>UPI Address</Label>
                  <Input
                    value={upi}
                    onChange={(e) => setUpi(e.target.value)}
                    placeholder="username@upi"
                  />
                </Field>

                <Field>
                  <Label>Sound</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setSound(1)} $on={sound === 1}>
                      On
                    </Toggle>
                    <Toggle onClick={() => setSound(0)} $on={sound === 0}>
                      Off
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>Vibration</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setVibration(1)} $on={vibration === 1}>
                      On
                    </Toggle>
                    <Toggle onClick={() => setVibration(0)} $on={vibration === 0}>
                      Off
                    </Toggle>
                  </ToggleRow>
                </Field>

                <Field>
                  <Label>Logo on Bill</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setShowLogo(1)} $on={showLogo === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setShowLogo(0)} $on={showLogo === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>QR Code on Bill</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setShowQr(1)} $on={showQr === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setShowQr(0)} $on={showQr === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                </Field>
                <Field>
                  <Label>Phone on Bill</Label>
                  <ToggleRow>
                    <Toggle onClick={() => setShowPhone(1)} $on={showPhone === 1}>
                      Yes
                    </Toggle>
                    <Toggle onClick={() => setShowPhone(0)} $on={showPhone === 0}>
                      No
                    </Toggle>
                  </ToggleRow>
                </Field>

                {/* <Field col="2">
                  <Subtle>Bill voice announcement</Subtle>
                  <ToggleRow>
                    <Toggle
                      onClick={() => setBillVoiceEnabled(true)}
                      $on={billVoiceEnabled === true}
                    >
                      Enabled
                    </Toggle>
                    <Toggle
                      onClick={() => setBillVoiceEnabled(false)}
                      $on={billVoiceEnabled === false}
                    >
                      Disabled
                    </Toggle>
                  </ToggleRow>
                  <LangRow
                    aria-disabled={!billVoiceEnabled}
                    $disabled={!billVoiceEnabled}
                  >
                    <LangChip
                      onClick={() => billVoiceEnabled && setBillVoiceLang("en")}
                      $active={billVoiceLang === "en"}
                    >
                      English
                    </LangChip>
                    <LangChip
                      onClick={() => billVoiceEnabled && setBillVoiceLang("hi")}
                      $active={billVoiceLang === "hi"}
                    >
                      हिन्दी
                    </LangChip>
                  </LangRow>
                </Field> */}
              </FormGrid>
            </ModalBody>

            <ModalActions>
              <CancelBtn onClick={() => setEditOpen(false)} disabled={saving}>
                Cancel
              </CancelBtn>
              <PrimaryBtn onClick={saveProfile} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </PrimaryBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* --- Delete Modal --- */}
      {confirmOpen && (
        <ModalOverlay
          onMouseDown={(e) =>
            e.target === e.currentTarget && setConfirmOpen(false)
          }
        >
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Confirm Account Deletion</ModalTitle>
              <CloseBtn
                onClick={() => setConfirmOpen(false)}
                aria-label="Close"
              >
                <FaTimes />
              </CloseBtn>
            </ModalHeader>

            <p style={{ color: "#6B7280", marginTop: 4 }}>
              This will permanently delete your account data from this device.
              This action cannot be undone.
            </p>

            <InfoBox>
              <InfoLine>
                <strong>Name:</strong> <span>{user?.name || "NA"}</span>
              </InfoLine>
              <InfoLine>
                <strong>Phone:</strong>{" "}
                <span>{user?.mobile_number || "NA"}</span>
              </InfoLine>
              <InfoLine>
                <strong>Email:</strong> <span>{user?.email || "NA"}</span>
              </InfoLine>
            </InfoBox>

            <ConfirmLabel>
              Type <b>DELETE</b> to confirm:
            </ConfirmLabel>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />

            <ModalActions>
              <CancelBtn
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </CancelBtn>
              <DangerBtn
                onClick={actuallyDeleteAccount}
                disabled={!confirmMatches || deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </DangerBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Wrap>
  );
};

export default ProfilePage;

/* ---------- small reusable ---------- */
const DataCard = ({ label, value }) => (
  <InfoCard>
    <InfoLabel>{label}</InfoLabel>
    <Value>{value ? value : "NA"}</Value>
  </InfoCard>
);

/* ---------- styled ---------- */
const Wrap = styled.div`
  background: ${color.bg};
  min-height: 100vh;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  background: ${color.brand};
  color: #fff;
  padding: 12px 16px;
`;
const IconBtn = styled.button`
  background: transparent;
  border: 0;
  color: inherit;
  font-size: 1.1rem;
  padding: 6px;
  cursor: pointer;
`;
const Title = styled.h1`
  margin: 0;
  flex: 1;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 0.2px;
`;
const ActionRow = styled.div`
  display: flex;
  gap: 10px;
`;
const Main = styled.main`
  margin: 20px auto;
  padding: 0 16px;
`;
const StatusCard = styled.div`
  background: ${({ $danger }) => ($danger ? "#FFF5F5" : "#ECFDF5")};
  border: 1px solid ${({ $danger }) => ($danger ? "#FECACA" : "#BBF7D0")};
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;
const StatusLeft = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;
const StatusTitle = styled.div`
  font-weight: 800;
  color: #1f2937;
`;
const StatusSub = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
`;
const PrimaryBtnSmall = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  background: ${color.brand};
  color: #fff;
  border: 0;
  font-weight: 800;
  cursor: pointer;
`;

const HeroCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  margin-bottom: 18px;
  box-shadow: 0 4px 16px rgba(2, 44, 77, 0.05);
`;
const Avatar = styled.div`
  width: 104px;
  height: 104px;
  border-radius: 50%;
  overflow: hidden;
  background: #ddd;
  margin: 0 auto 12px;
  border: 3px solid ${color.brand};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const Name = styled.h2`
  margin: 0;
  color: ${color.text};
  font-size: 1.4rem;
  font-weight: 900;
`;
const Shop = styled.p`
  margin: 4px 0 0;
  color: ${color.muted};
  font-weight: 600;
`;

const Badges = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
  justify-content: center;
`;
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${color.line};
  background: #fafbff;
  padding: 6px 10px;
  border-radius: 999px;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
`;

const Chips = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? color.brand : "#EFEFF3")};
  border: 1px solid ${({ $on }) => ($on ? color.brand : "#E6E8EE")};
`;
const ChipText = styled.span`
  color: ${({ $on }) => ($on ? "#fff" : "#444")};
  font-size: 12px;
  font-weight: 700;
`;

const Section = styled.section`
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(2, 44, 77, 0.04);
`;
const SectionTitle = styled.h3`
  margin: 4px 2px 10px;
  font-size: 0.85rem;
  color: ${color.muted};
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;
const InfoCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid ${color.line};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;
const InfoLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${color.brand};
`;
const Value = styled.div`
  margin-top: 4px;
  color: ${color.text};
  font-size: 1rem;
`;

const CtaRow = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;
const OutlineBtn = styled.button`
  flex: 1;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid ${color.brand};
  background: ${color.brandSoft};
  color: ${color.brand};
  font-weight: 800;
  cursor: pointer;
`;
const DangerOutlineBtn = styled(OutlineBtn)`
  border-color: ${color.danger};
  background: ${color.dangerSoft};
  color: ${color.danger};
`;
const ScanBtn = styled(OutlineBtn)`
  width: 100%;
  margin-top: 12px;
  justify-content: center;
  background: ${color.brand};
  color: #fff;
  border-color: ${color.brand};
  font-weight: 800;
  svg {
    color: #fff;
  }
`;

/* ---- Modal base ---- */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  padding: 22px;
  z-index: 1000;
`;
const ModalCard = styled.div`
  width: 100%;
  max-width: 820px;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;
const ModalTitle = styled.h4`
  margin: 0;
  font-size: 1.1rem;
  color: #1f2937;
  font-weight: 900;
`;
const CloseBtn = styled.button`
  background: transparent;
  border: 0;
  font-size: 1.1rem;
  cursor: pointer;
  color: #111;
`;
const ModalBody = styled.div`
  overflow-y: auto;
  padding-right: 6px;
  margin-top: 10px;
  flex: 1;
`;

/* ---- Edit form ---- */
const AvatarRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin: 8px 0 12px;
`;
const AvatarBig = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 16px;
  overflow: hidden;
  background: #eee;
  border: 2px solid ${color.brand};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .ph {
    width: 100%;
    height: 100%;
    background: #eee;
  }
`;
const HiddenFile = styled.input`
  display: none;
`;
const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${color.brand};
  color: #fff;
  cursor: pointer;
  font-weight: 700;
`;
const RemoveBtn = styled.button`
  margin-left: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${color.brand};
  background: #fff;
  color: ${color.brand};
  font-weight: 800;
  cursor: pointer;
`;
const Hint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Field = styled.div`
  grid-column: ${({ col }) => (col ? `span ${col}` : "span 1")};
  display: flex;
  flex-direction: column;
`;
const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${color.brand};
  margin-bottom: 4px;
`;
const Input = styled.input`
  border: 1px solid ${color.line};
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  background: #f9f9f9;
  color: ${color.text};
`;
const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
`;
const Toggle = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $on }) => ($on ? color.brand : color.line)};
  background: ${({ $on }) => ($on ? color.brand : "#fff")};
  color: ${({ $on }) => ($on ? "#fff" : color.text)};
  font-weight: 800;
  cursor: pointer;
`;
const Subtle = styled.div`
  font-size: 0.85rem;
  color: #546069;
  margin-bottom: 6px;
  margin-top: 6px;
`;
const LangRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 6px;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
`;
const LangChip = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${color.brand};
  background: ${({ $active }) => ($active ? color.brand : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : color.brand)};
  font-weight: 800;
  cursor: pointer;
`;

const ModalActions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
const CancelBtn = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: 0;
  background: #eef2ff;
  color: #374151;
  font-weight: 800;
  cursor: pointer;
`;
const PrimaryBtn = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: 0;
  background: ${color.brand};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
`;

/* ---- Delete modal specific ---- */
const InfoBox = styled.div`
  margin-top: 10px;
  background: #f7f8fc;
  border-radius: 12px;
  padding: 10px;
`;
const InfoLine = styled.div`
  color: #374151;
  margin-bottom: 4px;
  display: flex;
  gap: 6px;
  b,
  strong {
    color: #111827;
  }
  span {
    flex: 1;
  }
`;
const ConfirmLabel = styled.div`
  margin: 12px 0 6px;
  color: #374151;
  b {
    color: #e53935;
  }
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${color.danger};
`;
